import { useQuery } from '@tanstack/react-query';
import { Download, Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { getClassesWithMostOpeningAPI } from '~entities/classes/api/get-classes-with-most-opening';
import { getFullyBookedClassesAPI } from '~entities/classes/api/get-fully-booked-classes';

import { KEYS } from '~shared/api/query-keys';
import { downloadCSV } from '~shared/helpers/csv';
import { Button } from '~shared/shadcn/ui/button';

interface DownloadClassReportsProps {
  sessionId: string | undefined;
}

export const DownloadClassReports = ({
  sessionId,
}: DownloadClassReportsProps) => {
  const fullyBookedClassesQuery = useQuery({
    queryKey: [KEYS.FULLY_BOOKED_CLASSES, sessionId],
    queryFn: () =>
      getFullyBookedClassesAPI({ sessionId: sessionId ?? '', format: 'csv' }),
    enabled: false,
  });

  const classesWithMostOpeningQuery = useQuery({
    queryKey: [KEYS.CLASSES_WITH_MOST_OPENING, sessionId],
    queryFn: () =>
      getClassesWithMostOpeningAPI({
        sessionId: sessionId ?? '',
        format: 'csv',
      }),
    enabled: false,
  });

  const handleDownloadFullyBookedClasses = async () => {
    try {
      const { data } = await fullyBookedClassesQuery.refetch();
      const csvText =
        typeof data === 'string' && data ? data : 'No data available';
      downloadCSV(csvText, 'fully-booked-classes.csv');
      toast.success('Fully booked classes list downloaded');
    } catch (error) {
      toast.error('Error downloading fully booked classes list');
      console.error(error);
    }
  };

  const handleDownloadClassesWithMostOpening = async () => {
    try {
      const { data } = await classesWithMostOpeningQuery.refetch();
      const csvText =
        typeof data === 'string' && data ? data : 'No data available';
      downloadCSV(csvText, 'classes-with-most-opening.csv');
      toast.success('Classes with most openings list downloaded');
    } catch (error) {
      toast.error('Error downloading classes with most openings list');
      console.error(error);
    }
  };

  return (
    <div className='flex items-center gap-2'>
      <Button
        onClick={handleDownloadFullyBookedClasses}
        disabled={fullyBookedClassesQuery.isFetching}
        variant='outline'
      >
        {fullyBookedClassesQuery.isFetching ? (
          <Loader2Icon className='animate-spin' />
        ) : (
          <Download />
        )}
        Fully Booked Classes List
      </Button>
      <Button
        onClick={handleDownloadClassesWithMostOpening}
        disabled={classesWithMostOpeningQuery.isFetching}
        variant='outline'
      >
        {classesWithMostOpeningQuery.isFetching ? (
          <Loader2Icon className='animate-spin' />
        ) : (
          <Download />
        )}
        Classes with Most Openings List
      </Button>
    </div>
  );
};
